/**
 * Sale Order Agent SF (Salesforce) API Client
 * Uses /api/sale-sf/workflows endpoints
 */
class SFSaleApiClient {
  constructor(token) {
    this.token = token;
    this.baseUrl = process.env.REACT_APP_API_URL || 'https://nsgruve.ai.pocsolution.com';
    this.apiRoute = '/api/sale-sf/workflows';
  }

  /**
   * Start a new SF Sale Order workflow execution
   */
  async startWorkflow(workflowId, userMessage) {
    console.log('🔵 SFSaleAPI: Starting workflow', { workflowId, userMessage });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        workflow_id: parseInt(workflowId),
        user_message: userMessage,
        token: this.token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to start workflow');
    }

    console.log('✅ SFSaleAPI: Workflow started', data);
    return data;
  }

  /**
   * Continue an existing SF Sale Order workflow
   */
  async continueWorkflow(runId, userMessage) {
    console.log('🔵 SFSaleAPI: Continuing workflow', { runId, userMessage });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/continue/${runId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.token}`
      },
      body: JSON.stringify({
        user_message: userMessage,
        token: this.token
      })
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    if (!data.success) {
      throw new Error(data.error || 'Failed to continue workflow');
    }

    console.log('✅ SFSaleAPI: Workflow continued', data);
    return data;
  }

  /**
   * Get workflow execution status
   */
  async getWorkflowStatus(runId) {
    console.log('🔵 SFSaleAPI: Getting status', { runId });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/status/${runId}`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Get workflow events and chat messages
   */
  async getWorkflowEvents(runId, since = null) {
    console.log('🔵 SFSaleAPI: Getting events', { runId, since });

    let url = `${this.baseUrl}${this.apiRoute}/${runId}/events`;
    if (since) {
      url += `?since=${encodeURIComponent(since)}`;
    }

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Get current workflow state
   */
  async getCurrentState(workflowId) {
    console.log('🔵 SFSaleAPI: Getting current state', { workflowId });

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/${workflowId}/current-state`, {
      headers: {
        'Authorization': `Bearer ${this.token}`
      }
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.detail || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  }

  /**
   * Submit UI form
   */
  async submitUIForm(runId, formData) {
    console.log('🔵 SFSaleAPI: Submitting form', { runId });

    const form = new FormData();
    form.append('token', this.token);
    form.append('form_data', JSON.stringify(formData));

    const response = await fetch(`${this.baseUrl}${this.apiRoute}/ui-submit/${runId}`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${this.token}`
      },
      body: form
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(data.error || `HTTP ${response.status}: ${response.statusText}`);
    }

    return data;
  }
}

export default SFSaleApiClient;
